import { FormalizationDeadlineMode, FormalizationWindowDays } from '../../models/booking.model';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MS_PER_HOUR = 60 * 60 * 1000;
const MADRID_TZ = 'Europe/Madrid';

export function dateIsoInMadrid(ms = Date.now()): string {
  return new Date(ms).toLocaleDateString('en-CA', { timeZone: MADRID_TZ });
}

export function calendarDaysUntilEvent(
  eventDateIso: string,
  referenceMs = Date.now(),
): number {
  const refIso = dateIsoInMadrid(referenceMs);
  const [y, m, d] = eventDateIso.split('-').map(Number);
  const [yr, mr, dr] = refIso.split('-').map(Number);
  const eventUtc = Date.UTC(y, m - 1, d);
  const refUtc = Date.UTC(yr, mr - 1, dr);
  return Math.round((eventUtc - refUtc) / MS_PER_DAY);
}

export function eventStartMsMadrid(eventDateIso: string, startTime: string): number {
  return madridWallClockToUtc(eventDateIso, startTime);
}

export function formalizationWindowDays(daysUntilEvent: number): FormalizationWindowDays {
  if (daysUntilEvent > 30) return 7;
  if (daysUntilEvent > 15) return 3;
  if (daysUntilEvent >= 1) return 1;
  return 0;
}

/** UTC epoch ms for a Europe/Madrid wall-clock instant (YYYY-MM-DD + HH:mm). */
export function madridWallClockToUtc(eventDateIso: string, startTime: string): number {
  const [y, m, d] = eventDateIso.split('-').map(Number);
  const [hh, mm] = startTime.split(':').map(v => Number(v) || 0);

  let utcMs = Date.UTC(y, m - 1, d, hh, mm, 0, 0);
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: MADRID_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  for (let i = 0; i < 6; i++) {
    const parts = fmt.formatToParts(new Date(utcMs));
    const get = (type: string) => Number(parts.find(p => p.type === type)?.value ?? '0');
    const displayedMs = Date.UTC(
      get('year'),
      get('month') - 1,
      get('day'),
      get('hour'),
      get('minute'),
      get('second'),
    );
    const wantedMs = Date.UTC(y, m - 1, d, hh, mm, 0);
    const delta = wantedMs - displayedMs;
    if (delta === 0) break;
    utcMs += delta;
  }
  return utcMs;
}

export function resolveFormalizationWindowDays(
  eventDateIso: string,
  stored?: number | null,
  paymentMs?: number,
): FormalizationWindowDays {
  if (stored === 0 || stored === 1 || stored === 3 || stored === 7) return stored;
  const ref = paymentMs ?? Date.now();
  return formalizationWindowDays(calendarDaysUntilEvent(eventDateIso, ref));
}

export function resolveFormalizationDeadlineMode(
  stored?: FormalizationDeadlineMode | null,
  windowDays?: FormalizationWindowDays | null,
): FormalizationDeadlineMode {
  if (stored === 'event_minus_1h' || stored === 'payment_plus_days') return stored;
  return windowDays === 0 ? 'event_minus_1h' : 'payment_plus_days';
}

/** Human-readable plazo for headers and Mis reservas. */
export function formalizationPlazoLabel(
  windowDays: FormalizationWindowDays,
  mode: FormalizationDeadlineMode,
): string {
  if (mode === 'event_minus_1h' || windowDays === 0) {
    return 'hasta 1 hora antes del evento';
  }
  return `${windowDays} ${windowDays === 1 ? 'día' : 'días'} desde el pago`;
}

export function formalizationWorkflowHint(
  windowDays: FormalizationWindowDays,
  mode: FormalizationDeadlineMode,
): string {
  const plazo = formalizationPlazoLabel(windowDays, mode);
  return `Pago de gestión confirmado. Tienes ${plazo} para firmar el contrato y subir el justificante de la señal.`;
}

/** Urgent MANUAL formalization: event today or tomorrow (≤1 calendar day at payment). */
export function isFastTrackFormalizationEligible(booking: {
  daysUntilEventAtPayment?: number;
  formalizationDeadlineMode?: FormalizationDeadlineMode;
  formalizationWindowDays?: number;
}): boolean {
  if (typeof booking.daysUntilEventAtPayment === 'number') {
    return booking.daysUntilEventAtPayment <= 1;
  }
  if (booking.formalizationDeadlineMode === 'event_minus_1h') return true;
  return booking.formalizationWindowDays === 0;
}

/** Same rules as Cloud Functions `computeFormalizationDeadline`. */
export function computeFormalizationDeadline(
  eventDateIso: string,
  startTime: string,
  paymentMs = Date.now(),
): {
  deadlineMs: number;
  windowDays: FormalizationWindowDays;
  daysUntilEvent: number;
  deadlineMode: FormalizationDeadlineMode;
} {
  const daysUntilEvent = calendarDaysUntilEvent(eventDateIso, paymentMs);
  const windowDays = formalizationWindowDays(daysUntilEvent);

  if (windowDays === 0) {
    const eventStart = eventStartMsMadrid(eventDateIso, startTime);
    return {
      daysUntilEvent,
      windowDays: 0,
      deadlineMode: 'event_minus_1h',
      deadlineMs: eventStart - MS_PER_HOUR,
    };
  }

  const eventStart = eventStartMsMadrid(eventDateIso, startTime);
  const paymentDeadline = paymentMs + windowDays * MS_PER_DAY;
  const eventCap = eventStart - MS_PER_HOUR;

  return {
    daysUntilEvent,
    windowDays,
    deadlineMode: 'payment_plus_days',
    deadlineMs: Math.min(paymentDeadline, eventCap),
  };
}
